import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { FileText, Calculator, Percent, TrendingUp } from 'lucide-react';

const etapas = [
  { icon: FileText, label: 'Buscando documentos fiscais...' },
  { icon: Calculator, label: 'Processando segregação de receitas...' },
  { icon: Percent, label: 'Calculando alíquotas e tributos...' },
  { icon: TrendingUp, label: 'Apurando DAS a recolher...' },
];

const particulas = [
  { text: 'R$', left: '10%', delay: 0 },
  { text: '%', left: '80%', delay: 0.6 },
  { text: 'NF-e', left: '45%', delay: 1.1 },
  { text: '§', left: '62%', delay: 0.3 },
  { text: 'DAS', left: '25%', delay: 0.9 },
  { text: '×', left: '88%', delay: 1.4 },
  { text: 'COFINS', left: '5%', delay: 1.7 },
  { text: 'CSLL', left: '70%', delay: 0.15 },
];

export function SegregacaoLoading() {
  const [etapaAtual, setEtapaAtual] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setEtapaAtual(p => (p + 1) % etapas.length), 1800);
    return () => clearInterval(id);
  }, []);

  const { icon: Icon, label } = etapas[etapaAtual];

  return (
    <div className="relative flex flex-col items-center justify-center rounded-xl border bg-card py-20 overflow-hidden">
      {/* Partículas de fundo */}
      {particulas.map((p, i) => (
        <motion.span
          key={i}
          className="absolute text-[11px] font-mono font-bold text-foreground/[0.22] select-none pointer-events-none"
          style={{ left: p.left, bottom: '-10px' }}
          animate={{ y: [0, -440], opacity: [0, 0.9, 0] }}
          transition={{ duration: 5, delay: p.delay, repeat: Infinity, ease: 'linear' }}
        >
          {p.text}
        </motion.span>
      ))}

      {/* Ícone animado */}
      <motion.div
        className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted mb-6"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={etapaAtual}
            initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.6, rotate: 12 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <Icon className="h-6 w-6 text-muted-foreground" />
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Indicadores de etapa */}
      <div className="flex items-center gap-1.5 mb-5">
        {etapas.map((_, i) => (
          <motion.div
            key={i}
            className="h-1 rounded-full bg-foreground/20"
            animate={{
              width: i === etapaAtual ? 22 : 6,
              opacity: i === etapaAtual ? 1 : 0.35,
              backgroundColor: i === etapaAtual ? 'hsl(var(--foreground))' : undefined,
            }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          />
        ))}
      </div>

      {/* Texto da etapa */}
      <AnimatePresence mode="wait">
        <motion.p
          key={etapaAtual}
          className="text-sm font-medium text-foreground"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
        >
          {label}
        </motion.p>
      </AnimatePresence>

      <p className="text-xs text-muted-foreground mt-1.5">Isso pode levar alguns segundos...</p>

      {/* Barra de progresso indeterminada */}
      <div className="mt-6 w-44 h-0.5 rounded-full bg-foreground/10 overflow-hidden">
        <motion.div
          className="h-full w-1/2 rounded-full bg-foreground/30"
          animate={{ x: ['-100%', '200%'] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>
    </div>
  );
}
